import React, { useEffect, useRef } from 'react';
import { isPrerender } from '../utils/isPrerender';

interface FloatingElement {
  id: number;
  x: number;
  y: number;
  speedX: number;
  speedY: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  type: 'plus' | 'minus';
  opacity: number;
}

const AnimatedBackgroundLocal: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const elementsRef = useRef<FloatingElement[]>([]);
  const isVisibleRef = useRef<boolean>(true);
  const lastFrameTimeRef = useRef<number>(0);

  useEffect(() => {
    // Отключаем анимацию для Prerender
    if (isPrerender()) {
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { 
      alpha: true,
      desynchronized: true // Улучшает производительность анимации
    });
    if (!ctx) return;

    // Устанавливаем размер canvas равным размеру родительского элемента
    const resizeCanvas = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Intersection Observer для оптимизации (останавливаем анимацию когда не видно)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting;
          if (entry.isIntersecting && !animationRef.current) {
            animate();
          }
        });
      },
      { threshold: 0 } // Запускаем как только хоть что-то видно
    );

    observer.observe(canvas);

    // Убрано взаимодействие с мышью для экономии ресурсов
    // (можно раскомментировать если нужно)
    // let mouseThrottleTimeout: number | null = null;
    // const handleMouseMove = (e: MouseEvent) => {
    //   if (mouseThrottleTimeout) return;
    //   
    //   mouseThrottleTimeout = window.setTimeout(() => {
    //     const rect = canvas.getBoundingClientRect();
    //     const now = Date.now();
    //     mouseRef.current = {
    //       x: e.clientX - rect.left,
    //       y: e.clientY - rect.top,
    //       lastUpdate: now
    //     };
    //     mouseThrottleTimeout = null;
    //   }, 16); // ~60 FPS
    // };

    // canvas.addEventListener('mousemove', handleMouseMove, { passive: true });

    // Создаем плавающие элементы (оптимизировано: меньше элементов)
    const createElements = () => {
      const elements: FloatingElement[] = [];
      const isMobile = window.innerWidth < 768;
      const elementCount = isMobile 
        ? 2 // Минимум на мобильных
        : 3; // Уменьшено с 6 до 3 для экономии ресурсов

      for (let i = 0; i < elementCount; i++) {
        elements.push({
          id: i,
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          speedX: (Math.random() - 0.5) * 0.15, // Медленнее
          speedY: (Math.random() - 0.5) * 0.15,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 0.3, // Медленнее вращение
          scale: 0.5 + Math.random() * 0.3, // Меньше вариативность
          type: Math.random() > 0.5 ? 'plus' : 'minus',
          opacity: 0.1 + Math.random() * 0.1 // Чуть выше для видимости
        });
      }
      return elements;
    };

    elementsRef.current = createElements();

    // Функция отрисовки элемента (оптимизирована: меньше операций)
    const drawElement = (element: FloatingElement) => {
      ctx.save();
      ctx.translate(element.x, element.y);
      ctx.rotate((element.rotation * Math.PI) / 180);
      ctx.scale(element.scale, element.scale);
      ctx.globalAlpha = element.opacity;

      if (element.type === 'plus') {
        // Рисуем плюс (оптимизировано: один beginPath для всех линий)
        ctx.strokeStyle = 'rgba(34, 197, 94, 0.6)';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        // Вертикальная линия
        ctx.moveTo(0, -12);
        ctx.lineTo(0, 12);
        // Горизонтальная линия
        ctx.moveTo(-12, 0);
        ctx.lineTo(12, 0);
        // Круг вокруг
        ctx.arc(0, 0, 16, 0, 2 * Math.PI);
        ctx.stroke();
      } else {
        // Рисуем минус (оптимизировано: один beginPath)
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.6)';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        // Горизонтальная линия
        ctx.moveTo(-12, 0);
        ctx.lineTo(12, 0);
        // Круг вокруг
        ctx.arc(0, 0, 16, 0, 2 * Math.PI);
        ctx.stroke();
      }

      ctx.restore();
    };

    // Функция анимации с оптимизациями (FPS ограничение включено)
    const animate = (currentTime: number = 0) => {
      // Не анимируем если не видно
      if (!isVisibleRef.current) {
        animationRef.current = undefined;
        return;
      }

      // Ограничение FPS для экономии ресурсов (~30 FPS вместо 60)
      const deltaTime = currentTime - lastFrameTimeRef.current;
      if (deltaTime < 33) { // ~30 FPS
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrameTimeRef.current = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Упрощено: убрано взаимодействие с мышью для экономии ресурсов
      // const mouseX = mouseRef.current.x;
      // const mouseY = mouseRef.current.y;
      // const now = Date.now();
      // const mouseIsActive = (now - mouseRef.current.lastUpdate) < 2000;

      // Обновляем и отрисовываем элементы
      const elements = elementsRef.current;
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        
        // Естественное движение (реже для экономии)
        if (Math.random() < 0.01) { // Уменьшено с 0.015
          element.speedX += (Math.random() - 0.5) * 0.08;
          element.speedY += (Math.random() - 0.5) * 0.08;
        }

        // Обновляем позицию
        element.x += element.speedX;
        element.y += element.speedY;
        element.rotation += element.rotationSpeed;

        // Затухание скорости
        element.speedX *= 0.998;
        element.speedY *= 0.998;

        // Отскок от краев
        if (element.x < 0 || element.x > canvas.width) {
          element.speedX *= -0.8;
          element.x = Math.max(0, Math.min(canvas.width, element.x));
        }
        if (element.y < 0 || element.y > canvas.height) {
          element.speedY *= -0.8;
          element.y = Math.max(0, Math.min(canvas.height, element.y));
        }

        drawElement(element);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      // canvas.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ background: 'transparent', zIndex: 10 }}
    />
  );
};

export default AnimatedBackgroundLocal;
